// B3-02: the single most important part of explore mode. A high-visibility bar
// on every page, in a treatment used nowhere else on the platform, so nobody is
// ever unsure whether they are looking at their real nursery or the sample one.
// A real account never renders any of this.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { friendlyError } from "@/lib/friendlyError";
import { useExitExploreMode, useExploreMode } from "@/hooks/useExploreMode";
import { ProfessionalOnlyDialog } from "@/components/explore/ProfessionalOnlyDialog";

export function ExploreBanner() {
  const { exploring } = useExploreMode();
  const exit = useExitExploreMode();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [listOpen, setListOpen] = useState(false);

  if (!exploring) return null;

  const leave = async (thenUpgrade = false) => {
    try {
      await exit.mutateAsync();
      navigate(thenUpgrade ? "/upgrade" : "/dashboard");
    } catch (e) {
      toast({
        title: "Could not take you back just then",
        description: friendlyError(e),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="w-full border-b-2 border-[#92400E] bg-nursly-amber-light">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3">
          <div className="flex items-start gap-2 text-[#7C2D12]">
            <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div>
              <p className="text-sm font-semibold font-body">
                You are exploring with sample data. This is not your real nursery.
              </p>
              <button
                type="button"
                onClick={() => leave(true)}
                className="mt-0.5 text-[12px] font-light font-body text-[#92400E] underline underline-offset-4 hover:no-underline"
              >
                Upgrade to make this real for your nursery
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setListOpen(true)}
              className="inline-flex min-h-11 items-center rounded-md border border-[#92400E]/40 px-3 text-[13px] font-body text-[#7C2D12] hover:bg-white/60"
            >
              What is Professional-only?
            </button>
            <button
              type="button"
              onClick={() => leave(false)}
              disabled={exit.isPending}
              className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[#7C2D12] px-4 text-[13px] font-medium font-body text-white hover:bg-[#92400E] disabled:opacity-70"
            >
              {exit.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Return to my nursery
            </button>
          </div>
        </div>
      </div>

      <ProfessionalOnlyDialog open={listOpen} onOpenChange={setListOpen} />
    </>
  );
}
